import { Link } from "react-router-dom";
import { LegalLayout } from "./LegalLayout";

const LINK = "text-primary hover:underline";

/**
 * Privacy Policy page.
 *
 * Written to match what Spark does today, section by section: account and
 * content data, the LinkedIn / X / Meta connections, AI providers, public
 * profiles, retention and deletion.
 */
export function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      meta={
        <>
          <strong>Effective Date:</strong> February 28, 2026
          <br />
          <strong>Last Updated:</strong> 29 October 2026
        </>
      }
    >
      <p>
        Charisol (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
        operates the Spark by Charisol platform (&ldquo;Spark,&rdquo; &ldquo;the
        Service&rdquo;) accessible at{" "}
        <a href="https://spark.charisol.io" className={LINK}>
          spark.charisol.io
        </a>
        . This Privacy Policy explains what information we collect, how we use
        it, who we share it with, and the choices you have. It should be read
        with our{" "}
        <Link to="/terms" className={LINK}>
          Terms of Service
        </Link>
        .
      </p>
      <p>
        <strong>Who is responsible for your data.</strong> Charisol, a company
        registered in Nigeria, is the controller of the personal data you give
        Spark: we determine why and how it is used. We follow Nigeria&rsquo;s
        Data Protection Act 2023 and the Nigeria Data Protection
        Commission&rsquo;s (NDPC) implementation directive, and, for people in
        the EU, the UK and California, the GDPR, the UK GDPR and the CCPA/CPRA.
        You can reach the person responsible for data protection at{" "}
        <a href="mailto:privacy@charisol.io" className={LINK}>
          privacy@charisol.io
        </a>
        .
      </p>

      {/* ── 1. Information We Collect ── */}
      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <ul>
        <li>
          <strong>Account Information:</strong> Name, email address, and a
          password when you register (or the basic profile your Google account
          shares with us if you sign in with Google).
        </li>
        <li>
          <strong>Profile &amp; Persona Data:</strong> Display name, bio, tone
          preferences, writing examples, and brand voice settings you configure.
        </li>
        <li>
          <strong>Content:</strong> Articles, notes, ideas, research, text,
          uploaded files and other source materials you submit, as well as the
          AI-generated drafts and the edits you make to them.
        </li>
        <li>
          <strong>Layers Imports:</strong> Writing and records you bring in from
          your own sources: meeting transcripts, journals, notes, files, GitHub
          activity and YouTube videos. These can contain information about other
          people and sensitive information. See Section 2.2.
        </li>
        <li>
          <strong>Chat:</strong> The threads you have with Spark&rsquo;s chat
          assistant, and what it remembers to help you.
        </li>
        <li>
          <strong>Billing:</strong> Your plan, credit balance and payment
          records. When paid plans are switched on, card payments are handled by
          Stripe (Section 7).
        </li>
        <li>
          <strong>Communications:</strong> Information you provide when
          contacting support or sending feedback, including any attachments.
        </li>
      </ul>

      <h3>1.2 Information from Connected Social Accounts</h3>
      <p>
        When you connect a social account, we receive the information that
        platform authorizes. Section 3 describes this in detail for LinkedIn, X
        and Meta (Facebook, Instagram and Threads), and Section 3.5 covers
        GitHub, YouTube and WordPress.
      </p>

      <h3>1.3 Automatically Collected Information</h3>
      <ul>
        <li>
          <strong>Usage Data:</strong> Features used, generations and credits
          consumed, actions taken and timestamps, so we can apply your
          plan&rsquo;s limits and bill correctly.
        </li>
        <li>
          <strong>Device and Request Information:</strong> Browser type,
          operating system and language, as sent with ordinary web requests.
        </li>
        <li>
          <strong>Cookies &amp; Browser Storage:</strong> See Section 10.
        </li>
      </ul>

      {/* ── 2. How We Use ── */}
      <h2>2. How We Use Your Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service.</li>
        <li>
          Authenticate you and manage your account and connected social
          accounts.
        </li>
        <li>
          Generate AI-powered drafts based on your source materials and persona
          settings.
        </li>
        <li>
          Publish content to connected platforms only when you explicitly ask us
          to, immediately or at a time you schedule.
        </li>
        <li>
          Send transactional emails such as verification codes, password resets,
          publishing results and account notifications.
        </li>
        <li>Meter usage, apply plan limits and handle billing.</li>
        <li>Improve the Service using aggregated, anonymized figures.</li>
        <li>Respond to your support requests and inquiries.</li>
        <li>
          Enforce our Terms of Service and protect against misuse or fraud.
        </li>
      </ul>

      <h3>2.1 Our legal bases</h3>
      <p>
        Where the law asks us to name a basis for using your data, these are
        ours:
      </p>
      <ul>
        <li>
          <strong>Contract:</strong> providing the Service you signed up for:
          your account, drafts, AI generation, publishing you ask for, billing
          and support you request.
        </li>
        <li>
          <strong>Legitimate interests:</strong> keeping Spark secure, stopping
          misuse and fraud, measuring which of our own marketing brought you
          here (Section 10), improving the Service with aggregated figures, and
          the limited staff access in Section 11.1. We weigh these against your
          interests and rights, and you can object (Section 12).
        </li>
        <li>
          <strong>Consent:</strong> sensitive content in Layers imports (Section
          2.2), and marketing emails. You can withdraw consent at any time.
        </li>
        <li>
          <strong>Legal obligation:</strong> where the law requires us to keep
          or hand over information.
        </li>
      </ul>

      <h3 id="layers">2.2 Layers imports and sensitive information</h3>
      <p>
        Layers lets you bring in meeting transcripts, journals and other
        material of your own. These can contain information about other people
        (colleagues in a transcript, family in a journal) and sensitive
        information such as health, beliefs or political views. We process
        Layers material only to serve you: to build your timeline and shape your
        drafts. We do not use it for advertising, we do not sell it, and we do
        not use it to train models.
      </p>
      <p>
        You tell us before each import that you may bring the material in, and
        your consent covers any sensitive content in it. You can withdraw it by
        deleting the import or the layer, or by deleting your account. You are
        responsible for importing only material you are allowed to use; see our{" "}
        <Link to="/terms" className={LINK}>
          Terms of Service
        </Link>
        . Our staff never see your Layers (Section 11.1).
      </p>

      {/* ── 3. Social media connections ── */}
      <h2>3. Social Media Connections</h2>
      <p>
        Spark lets you connect social accounts so that posts you have written
        and approved can be published there. Every connection is made through
        the platform&rsquo;s own OAuth sign-in screen. We never see or store
        your password for LinkedIn, X, Facebook, Instagram or Threads, and you
        choose what to grant on the platform&rsquo;s consent screen.
      </p>

      <h3>3.1 What we access</h3>
      <ul>
        <li>
          <strong>LinkedIn:</strong> Your name, email address, profile picture
          and LinkedIn member ID (OpenID Connect: <code>openid</code>,{" "}
          <code>profile</code>, <code>email</code>), and permission to create
          and delete posts on your behalf (<code>w_member_social</code>), which
          includes uploading images, video and documents that you attach to a
          post.
        </li>
        <li>
          <strong>X:</strong> Your X username, name and profile picture, and
          permission to read and write posts for your account (
          <code>users.read</code>, <code>tweet.read</code>,{" "}
          <code>tweet.write</code>), plus a refresh token (
          <code>offline.access</code>) so scheduled posts can be published
          without asking you to sign in again. Media you attach is uploaded to X
          when you publish.
        </li>
        <li>
          <strong>Meta (Facebook, Instagram and Threads):</strong> Spark uses
          the Meta Platform in accordance with Meta&rsquo;s{" "}
          <a
            href="https://developers.facebook.com/terms"
            className={LINK}
            target="_blank"
            rel="noreferrer"
          >
            Platform Terms
          </a>{" "}
          and{" "}
          <a
            href="https://developers.facebook.com/devpolicy"
            className={LINK}
            target="_blank"
            rel="noreferrer"
          >
            Developer Policies
          </a>
          . When you connect a Facebook Page, an Instagram professional account
          or a Threads profile, we receive your Meta account ID and name, and
          for each Page or account you select, its ID, name, profile picture and
          a Page- or account-scoped access token. We use these only to publish
          posts you create in Spark to the Page or account you selected, and to
          show you how those posts performed. We request only the permissions
          needed for this (<code>pages_show_list</code>,{" "}
          <code>pages_read_engagement</code> and <code>pages_manage_posts</code>{" "}
          for Facebook Pages; <code>instagram_basic</code> and{" "}
          <code>instagram_content_publish</code> for Instagram;{" "}
          <code>threads_basic</code> and <code>threads_content_publish</code>{" "}
          for Threads). We do not receive your Facebook friends list, your
          personal timeline, or private messages.
        </li>
      </ul>

      <h3>3.2 What we store</h3>
      <ul>
        <li>
          Access and refresh tokens, stored encrypted at rest and used only to
          perform actions you request.
        </li>
        <li>
          Profile basics returned by the platform: display name or username,
          profile picture URL, the platform account or Page ID, and (for
          LinkedIn) your email address.
        </li>
        <li>
          For each post published through Spark: the post&rsquo;s ID on the
          platform, its link, its publish time and status.
        </li>
        <li>
          Where a platform allows it, metrics for your own published posts (such
          as impressions, reactions, comments and shares), so we can show them
          to you inside Spark.
        </li>
      </ul>

      <h3>3.3 What we never do</h3>
      <ul>
        <li>
          We never post, schedule, edit or delete anything on your social
          accounts unless you take an explicit action in Spark (publishing now,
          scheduling, or deleting a post you published through Spark).
        </li>
        <li>
          We do not read your private messages, your connections or follower
          lists, or other people&rsquo;s personal data.
        </li>
        <li>
          We do not sell social platform data, use it for advertising, or
          combine it with data from other sources to profile you.
        </li>
        <li>We do not use data from Meta, LinkedIn or X to train AI models.</li>
      </ul>

      <h3>3.4 Disconnecting</h3>
      <p>
        You can disconnect any social account at any time from{" "}
        <strong>Settings &rarr; Connections</strong> in Spark. Disconnecting
        deletes the stored tokens and profile details for that connection
        immediately, and Spark can no longer act on that account. Posts already
        published to the platform stay there, because they belong to your
        account on that platform; delete them on the platform if you want them
        gone. You can also remove Spark&rsquo;s access from the platform itself:
      </p>
      <ul>
        <li>
          LinkedIn: Settings &amp; Privacy &rarr; Data privacy &rarr; Permitted
          services.
        </li>
        <li>
          X: Settings &rarr; Security and account access &rarr; Apps and
          sessions &rarr; Connected apps.
        </li>
        <li>
          Facebook and Instagram: Settings &rarr; Business Integrations (or Apps
          and Websites), then Remove for Spark.
        </li>
        <li>Threads: Settings &rarr; Account &rarr; Website permissions.</li>
      </ul>
      <p>
        Removing Spark in Facebook&rsquo;s settings ends its access to your
        Facebook data. To have the data Spark already holds deleted as well,
        follow the steps on our{" "}
        <Link to="/data-deletion" className={LINK}>
          data deletion instructions
        </Link>{" "}
        page.
      </p>

      <h3>3.5 GitHub, YouTube and WordPress</h3>
      <ul>
        <li>
          <strong>GitHub:</strong> Used to import your work as Layers moments.
          We read pull request titles, descriptions, labels, repository names
          and dates, and the messages of direct commits for the repositories you
          pick. We never read your code, diffs, or issue and review comments.
        </li>
        <li>
          <strong>YouTube:</strong> Used to import your own videos and captions
          as Layers moments, and, only when you ask, to update the description
          of one of your videos. The permission to edit is requested at sign-in
          because Google bundles it with reading captions; you can decline it on
          Google&rsquo;s screen.
        </li>
        <li>
          <strong>WordPress:</strong> Used to send a post you have written to
          your site as a draft or published post, only when you choose to. We
          keep the site address and a token or application password, encrypted,
          until you disconnect.
        </li>
      </ul>

      {/* ── 4. AI processing ── */}
      <h2>4. AI Processing</h2>
      <p>
        Spark generates and edits drafts with large language models run by
        third-party providers. When you generate, refine, or ask for feedback on
        a draft, the text needed for that request is sent to one of these
        providers over an encrypted connection:
      </p>
      <ul>
        <li>
          <strong>Anthropic</strong> (Claude models).
        </li>
        <li>
          <strong>Google</strong> (Gemini models, and other hosted models, via
          Google Cloud Vertex AI).
        </li>
        <li>
          <strong>OpenAI</strong> (GPT models).
        </li>
      </ul>
      <p>
        What is sent is the content of the request: your source material, your
        instructions, and the persona and voice settings that shape the output.
        We do not send your name, email address or account identifiers to these
        providers. Spark chooses the provider and model for each request; you do
        not need an account with any of them.
      </p>
      <p>
        We use these providers through their commercial APIs. As far as their
        API terms state, they do not use content submitted through the API to
        train their models by default, and we do not opt in to any setting that
        would allow it. We do not use your content to train models of our own.
        Those terms are the providers&rsquo; and can change; see their privacy
        policies for how they handle data on their side, including any
        short-term retention for abuse monitoring.
      </p>
      <p>
        AI output can be wrong or resemble existing text. You review and approve
        everything before it is published.
      </p>

      <h3>4.1 Dictation (voice input)</h3>
      <p>
        If you use dictation, the audio you record is sent to a speech-to-text
        provider (OpenAI) to be turned into text, and the text is placed in your
        draft. The audio is sent for the duration of the request only and is not
        stored by Spark. OpenAI is our current transcription provider.
      </p>

      {/* ── 5. Public profiles ── */}
      <h2>5. Public Profiles &amp; the Voice Gallery</h2>
      <p>
        Your profile is private by default. Your profile page can be reached,
        and indexed by search engines, only when you set it to Public. If you
        choose to publish something to other Spark users, such as a voice in the
        voice gallery, Spark asks you to set up a public profile for the persona
        you are publishing under. A public profile consists of:
      </p>
      <ul>
        <li>a public name and a handle (unique across Spark),</li>
        <li>an avatar and a one-line bio, and</li>
        <li>
          the voices you have published, and, only if you turn it on, your
          published posts.
        </li>
      </ul>
      <p>
        These are shown next to anything you publish, to other Spark users and
        potentially to anyone who visits your profile page. Your private persona
        label, email address and drafts are never shown. You can unpublish
        items, and remove your public profile once nothing is published under
        it; the handle is then released after 30 days.
      </p>

      {/* ── 6. Sharing ── */}
      <h2>6. How We Share Your Information</h2>
      <p>
        We do <strong>not</strong> sell, rent, or trade your personal
        information. We share it only in these limited circumstances:
      </p>
      <ul>
        <li>
          <strong>Service Providers:</strong> Vendors that help us run the
          Service: cloud hosting and storage (Amazon Web Services), account
          sign-in (Amazon Cognito), Maildrip, which sends account and product
          email, Cloudflare (website delivery and security), Stripe when paid
          plans are on, and the AI providers in Section 4. They process data on
          our behalf under their own terms.
        </li>
        <li>
          <strong>Social Platforms:</strong> When you publish, the content you
          approve (and any media attached) is sent to the platform you selected.
        </li>
        <li>
          <strong>Other Spark users:</strong> Only what you choose to publish
          under a public profile (Section 5).
        </li>
        <li>
          <strong>Legal Obligations:</strong> We may disclose information if
          required by law, legal process, or a governmental request.
        </li>
        <li>
          <strong>Business Transfers:</strong> In the event of a merger,
          acquisition, or sale of assets, your information may be transferred as
          part of that transaction, and we will tell you if our privacy
          practices change.
        </li>
      </ul>

      {/* ── 7. Payments ── */}
      <h2>7. Payments</h2>
      <p>
        Paid plans and credits are billed through a payment processor such as
        Stripe. Your card details are entered with the processor and never touch
        Spark&rsquo;s servers. We keep a record of your plan, your credit
        balance and the payments made.
      </p>

      {/* ── 8. Retention ── */}
      <h2>8. Data Retention</h2>
      <p>
        We keep your data for as long as your account is active or as needed to
        provide the Service. Specifically:
      </p>
      <ul>
        <li>
          <strong>Account Data:</strong> Kept until you delete your account.
        </li>
        <li>
          <strong>Content &amp; Drafts:</strong> Kept until you delete them or
          delete your account.
        </li>
        <li>
          <strong>Draft History:</strong> Earlier versions of a draft are kept
          for 30 days on the Free plan and one year on Pro; Team and Enterprise
          keep them without a time limit. Versions you have named, and the
          version saved just before each publish, are always kept for as long as
          the draft exists.
        </li>
        <li>
          <strong>Layers Imports &amp; Chat:</strong> Kept until you delete them
          or delete your account.
        </li>
        <li>
          <strong>Staff Access Records:</strong> Each time our staff open your
          account details or content, we record who, when, what and why. We keep
          these records for 2 years. They are also kept, with your identity
          replaced, after you delete your account, until that period ends.
        </li>
        <li>
          <strong>Social Connection Tokens:</strong> Deleted when you disconnect
          the account or delete your Spark account.
        </li>
        <li>
          <strong>Usage Records:</strong> Counts of generations, credits used
          and AI processing costs are kept while your account is active, so we
          can apply your plan&rsquo;s limits and bill correctly. When you delete
          your account they are anonymized, no longer linked to you, and kept
          for billing, accounting and service improvement.
        </li>
        <li>
          <strong>Short-lived Records:</strong> Some operational records, such
          as sign-in codes and notification inbox entries, expire automatically
          after minutes, days or up to 90 days.
        </li>
      </ul>

      {/* ── 9. Deletion ── */}
      <h2>9. Deleting Your Data</h2>
      <p>
        You can delete your account yourself in{" "}
        <strong>Settings &rarr; Account &rarr; Delete account</strong>. After
        you confirm with an emailed code, your password (if you have one) and
        your email address, deletion starts immediately and cannot be undone. It
        removes your profile, personas, drafts and posts, ideas, research,
        uploaded and generated files, connected social accounts (we delete the
        stored tokens and, where we can, ask the platform to revoke them),
        scheduled posts and sign-in accounts. We then email you a confirmation.
        If a step fails, our system retries it until the deletion is complete.
      </p>
      <p>
        The only things kept are anonymized usage, billing and audit records
        (counts and dates, with your identity replaced by a random identifier
        that is not stored alongside your user ID), as described in Section 8.
        Content that other people have already seen, such as a published social
        post, is not removed from the platform it was published to.
      </p>
      <p>
        If you cannot sign in, or want to ask about Facebook, Instagram or
        Threads data specifically, see our{" "}
        <Link to="/data-deletion" className={LINK}>
          data deletion instructions
        </Link>
        .
      </p>

      {/* ── 10. Cookies ── */}
      <h2>10. Cookies &amp; Browser Storage</h2>
      <p>Spark uses a minimal set of cookies and browser storage:</p>
      <ul>
        <li>
          <strong>Sign-in:</strong> Session tokens are kept in your
          browser&rsquo;s storage so you stay signed in. One lasts for the
          browser tab; the refresh token persists until you sign out.
        </li>
        <li>
          <strong>Preferences:</strong> Your theme and a few interface settings
          (for example which tips you have dismissed) are stored in your
          browser.
        </li>
        <li>
          <strong>First-Touch Cookie:</strong> A first-party cookie, kept for 30
          days, that remembers how you first arrived at Spark (such as a
          campaign link or a referral code) so that, if you sign up, we know
          which of our own marketing efforts brought you here. It identifies
          nothing about you until you create an account, and is deleted along
          with your account if you delete it.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> use third-party advertising or tracking
        cookies. You can clear or block cookies and site data in your browser,
        but you will then be signed out and some features may not work.
      </p>

      {/* ── 11. Security ── */}
      <h2>11. Data Security</h2>
      <p>We protect your data with measures including:</p>
      <ul>
        <li>Encryption in transit (TLS/HTTPS) and at rest for stored data.</li>
        <li>
          Encrypted storage of OAuth tokens, and no storage of your social media
          passwords at all.
        </li>
        <li>
          Access controls limiting which systems and staff can reach your data,
          and multi-factor sign-in for staff.
        </li>
      </ul>

      <h3 id="staff">11.1 Staff access to your content</h3>
      <p>
        Our staff can see metadata about your drafts and posts, such as titles,
        dates and status. They can read a draft&rsquo;s text only after
        recording a reason, such as your own support request. That access lasts
        15 minutes for each reason, and every such access is logged. Posts you
        have published can be seen by staff. Sources you import are shown to
        staff redacted by default, and staff can reveal one only with a recorded
        reason. Staff never see your Layers or your Chat. Staff are bound by
        confidentiality duties and use this access only to help you, to
        investigate security or abuse, or to meet a legal duty.
      </p>
      <p>
        No method of transmission or storage is completely secure. If you find a
        security problem, please tell us at{" "}
        <a href="mailto:info@charisol.io" className={LINK}>
          info@charisol.io
        </a>
        .
      </p>

      {/* ── 12. Rights ── */}
      <h2>12. Your Rights &amp; Choices</h2>
      <p>Depending on where you live, you may have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> a copy of the personal data we hold about you.
        </li>
        <li>
          <strong>Correct</strong> inaccurate or incomplete data (most of it you
          can edit directly in Spark).
        </li>
        <li>
          <strong>Delete</strong> your personal data and account (Section 9).
        </li>
        <li>
          <strong>Export</strong> your data in a machine-readable format. Today
          we do this on request, by email.
        </li>
        <li>
          <strong>Object</strong> to processing based on our legitimate
          interests, and ask us to restrict processing.
        </li>
        <li>
          <strong>Disconnect</strong> any connected social account at any time
          (Section 3.4).
        </li>
        <li>
          <strong>Withdraw consent</strong> where processing is based on
          consent, without affecting processing already done.
        </li>
      </ul>
      <p>
        To exercise a right, email{" "}
        <a href="mailto:privacy@charisol.io" className={LINK}>
          privacy@charisol.io
        </a>
        . We will respond within 30 days. If you are unhappy with our answer,
        you can complain to the Nigeria Data Protection Commission (
        <a
          href="https://ndpc.gov.ng"
          className={LINK}
          target="_blank"
          rel="noreferrer"
        >
          ndpc.gov.ng
        </a>
        ) or to the data protection authority where you live or work. If you
        live in California, you can also ask what we collect and ask us to
        delete it; we do not sell or share personal information for advertising.
      </p>

      {/* ── 13. Children ── */}
      <h2>13. Children&rsquo;s Privacy</h2>
      <p>
        Spark is not directed to individuals under the age of 16. We do not
        knowingly collect personal information from children. If we learn that
        we have collected data from a child, we will delete it promptly.
      </p>

      {/* ── 14. International Transfers ── */}
      <h2>14. International Data Transfers</h2>
      <p>
        Charisol is based in Nigeria and Spark has users worldwide. Your data is
        hosted in Amazon Web Services&rsquo; Stockholm region in Sweden. The
        companies that process data for us are Amazon Web Services (hosting and
        sign-in), Google (Vertex AI), Anthropic and OpenAI (AI generation and
        dictation), Maildrip (email), Stripe (payments, when enabled) and
        Cloudflare (website delivery). The AI providers and the social platforms
        you connect process data in the United States and the European Union
        under their own terms. That means your information may be processed
        outside your country of residence. Where the law requires a safeguard
        for such transfers, we rely on appropriate safeguards as provided by
        each processor.
      </p>

      {/* ── 15. Changes ── */}
      <h2>15. Changes to This Privacy Policy</h2>
      <p>
        We may update this policy. When we do, we will change the &ldquo;Last
        Updated&rdquo; date above. For material changes we will notify you by
        email or with a notice in the Service.
      </p>

      {/* ── 16. Contact ── */}
      <h2>16. Contact Us</h2>
      <p>
        For questions, concerns, or requests about this policy or our data
        practices:
      </p>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:info@charisol.io" className={LINK}>
            info@charisol.io
          </a>
        </li>
        <li>
          <strong>Data protection:</strong>{" "}
          <a href="mailto:privacy@charisol.io" className={LINK}>
            privacy@charisol.io
          </a>
        </li>
        <li>
          <strong>Company:</strong> Charisol, Nigeria
        </li>
        <li>
          <strong>Website:</strong>{" "}
          <a href="https://charisol.io" className={LINK}>
            charisol.io
          </a>
        </li>
      </ul>
    </LegalLayout>
  );
}
